/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSelectAll';

const ActionSelectAllManager: ActionManagerFn<'ActionSelectAll'> = (
  platform: Platform,
): ActionManager<'ActionSelectAll'> => {
  return {
    ...baseActionManager(platform, 'ActionSelectAll'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
    },
    name: 'Select All Records',
    getDisplayText: (item) => item.props.alias,
    replaceableWith: ['ActionDeselectAll'],
    icon: { iconName: 'check-square' },
    description: `**Overview**<br/>
The Select All Records action is a bulk state management component designed to programmatically apply a selection state to every single record currently loaded in a Data Store. It functions as a "Select All" command, eliminating the need for users to manually check boxes for individual items when they intend to perform an operation on the entire dataset.<br/><br/>
This component modifies the internal state of the Data Store. Once triggered, every record in the target alias is marked as "selected," and any UI components bound to that store (such as Data Grids or Lists) will immediately visually reflect this change (e.g., all checkboxes becoming checked).

**Key Features**<br/>
* **Comprehensive Selection:** Instantly marks every record in the store as selected, regardless of how many records are currently loaded.
* **Broad Store Compatibility:** Supports a wide range of store types including CloudDataStore, DataStore, LocalDataStore, and ReadOnlyDataStore, making it versatile for both server-side and local collections.
* **Component Interchangeability:** Explicitly configured to be easily swappable with ActionDeselectAll, allowing developers to toggle logic on a single button (e.g., changing "Select All" to "Deselect All" after it's clicked).
* **UI Synchronization:** Automatically triggers updates in all bound interface elements, ensuring the visual state stays in sync with the data model.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property.
  * *Function:* Identifies the specific Data Store where all records should be selected.
  * *Constraint:* Must be a valid data store type (Cloud, Local, ReadOnly, etc.).

**Use Cases**<br/>
* **Master Checkbox:** Implementing the logic for a checkbox in a grid header that selects all rows when clicked.
* **Batch Preparation:** A "Process All" button that first selects every pending order in the list and then triggers a batch update workflow.
* **Quick Actions:** Allowing a user to quickly "Select All" items in a shopping cart to move them to a wish list.`,
  };
};

export default ActionSelectAllManager;
